import { useEffect, useMemo, useRef, useState } from "react";

import {
  Alert,
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Rating,
  TextField,
  Typography,
} from "@mui/material";

import { useSearchParams } from "react-router-dom";

import { FaCheck, FaClipboardList, FaExclamationTriangle, FaRedo } from "react-icons/fa";

import VuiButton from "components/VuiButton";
import OverlayLoader from "components/OverlayLoader";
import useCommon from "hooks/useCommon";

import {
  resetSubmitSatisfactionFormState,
  resetValidateSatisfactionReservationState,
  submitSatisfactionForm,
  validateSatisfactionReservation,
} from "store/slice/satisfactionSlice";

const SARTO = {
  green: "#1B2E24",
  cloud: "#ECECEC",
  mint: "#F4F7F5",
  gold: "#D4A017",
  white: "#FFFFFF",
  text: "#24342B",
  muted: "#728078",
  border: "#DBE3DD",
  progressBg: "#DFE8E2",
  error: "#D94B4B",
  warning: "#B98736",
  success: "#2A7651",
};

const getQuestionId = (question) => question?.questionId || question?.id || question?._id;

const getQuestionText = (question) => question?.questionText || question?.question || "";

const getQuestionType = (question) => {
  const type = question?.questionType || question?.type || "";

  if (type === "rating_scale") return "rate_scale";

  if (type === "long_text") return "text_input";

  return type;
};

const getOptionId = (option) => {
  if (typeof option === "string") return option;

  return option?.optionId || option?.id || option?._id || option?.label;
};

const getOptionLabel = (option) => {
  if (typeof option === "string") return option;

  return option?.label || "";
};

const getApiErrorMessage = (apiError, fallbackMessage) => {
  if (!apiError) return fallbackMessage;

  if (typeof apiError === "string") return apiError;

  return apiError?.message || apiError?.response?.data?.message || fallbackMessage;
};

// ------------------------------------------------------
// SHARED PAGE LAYOUT
// ------------------------------------------------------

const SatisfactionPageLayout = ({ children, centered = false }) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        "@supports (min-height: 100dvh)": {
          minHeight: "100dvh",
        },
        width: "100%",
        backgroundColor: SARTO.mint,
        display: "flex",
        flexDirection: "column",
        fontFamily: "inherit",
      }}
    >
      {/*  green header */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 42, md: 58 },
          flexShrink: 0,
          backgroundColor: SARTO.green,
        }}
      />

      {/* Full-width mint background */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          backgroundColor: SARTO.mint,
        }}
      >
        {/* Centered form content */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 810,
            minWidth: 0,
            boxSizing: "border-box",
            flexShrink: 0,
            ...(centered && {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }),
            backgroundColor: SARTO.mint,
            px: {
              xs: 2,
            },
            py: {
              xs: 2,
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

// ------------------------------------------------------
// STATUS STATE
// ------------------------------------------------------

const StatusState = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  type = "error",
  centered = true,
}) => {
  const stateColor =
    type === "success" ? SARTO.success : type === "warning" ? SARTO.warning : SARTO.error;

  return (
    <SatisfactionPageLayout centered={centered}>
      <Card
        sx={{
          width: "100%",
          maxWidth: 650,
          mx: "auto",
          mt: centered
            ? 0
            : {
                xs: 2,
                md: 4,
              },
          px: {
            xs: 3,
            md: 5,
          },
          py: {
            xs: 4,
            md: 5,
          },
          borderRadius: "18px",
          backgroundColor: SARTO.white,
          border: `1px solid ${SARTO.border}`,
          boxShadow: "0 4px 20px rgba(27,46,36,0.025)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            mx: "auto",
            mb: 2.5,
            backgroundColor: `${stateColor}12`,
            border: `1px solid ${stateColor}30`,
            color: stateColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontSize: {
              xs: "22px",
              md: "26px",
            },
            fontWeight: 600,
            color: SARTO.green,
            letterSpacing: "-0.5px",
            mb: 1.2,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: 1.8,
            color: SARTO.muted,
            maxWidth: 440,
            mx: "auto",
          }}
        >
          {description}
        </Typography>

        {actionLabel && onAction && (
          <VuiButton
            onClick={onAction}
            startIcon={<FaRedo size={12} />}
            sx={{
              mt: 3,
              backgroundColor: `${SARTO.green} !important`,
              color: "#fff !important",
              borderRadius: "10px",
              textTransform: "none",
              px: 3,
              py: 1.2,
              fontSize: "13px",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#294436 !important",
              },
            }}
          >
            {actionLabel}
          </VuiButton>
        )}
      </Card>
    </SatisfactionPageLayout>
  );
};

// ------------------------------------------------------
// MAIN COMPONENT
// ------------------------------------------------------

const GuestSatisfactionFormSubmission = () => {
  const { DISPATCH, STORE } = useCommon();

  const [searchParams] = useSearchParams();

  const reservationId = searchParams.get("reservationId") || searchParams.get("rid") || "";

  const {
    publicValidationResult,
    validateReservationLoading,
    validateReservationError,

    submissionResult,
    submitFormLoading,
    submitFormSuccess,
    submitFormError,
  } = STORE((state) => state.satisfaction);

  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [pageError, setPageError] = useState("");

  const questionRefs = useRef({});

  const form = publicValidationResult?.form || null;

  // ----------------------------------------------------
  // QUESTIONS
  // ----------------------------------------------------

  const questions = useMemo(() => {
    const formQuestions = Array.isArray(form?.questions) ? form.questions : [];

    return [...formQuestions]
      .filter((question) => question?.isActive !== false)
      .sort((first, second) => Number(first?.sortOrder || 0) - Number(second?.sortOrder || 0));
  }, [form]);

  const totalQuestions = questions.length;

  const hasAnswer = (question, value) => {
    const questionType = getQuestionType(question);

    if (questionType === "rate_scale") {
      const ratingValue = Number(value);

      return ratingValue >= 1 && ratingValue <= 5;
    }

    if (questionType === "multiple_choice") {
      return Array.isArray(value) && value.length > 0;
    }

    if (questionType === "text_input") {
      return String(value || "").trim().length > 0;
    }

    return String(value || "").trim().length > 0;
  };

  const answeredCount = useMemo(() => {
    return questions.filter((question) => {
      const questionId = getQuestionId(question);

      const questionType = getQuestionType(question);

      const value = answers[questionId];

      if (questionType === "rate_scale") {
        return Number(value) >= 1 && Number(value) <= 5;
      }

      if (questionType === "multiple_choice") {
        return Array.isArray(value) && value.length > 0;
      }

      return String(value || "").trim().length > 0;
    }).length;
  }, [answers, questions]);

  const completionPercent =
    totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  // ----------------------------------------------------
  // LOAD FORM
  // ----------------------------------------------------

  const loadForm = async () => {
    if (!reservationId) return;

    setAnswers({});
    setErrors({});
    setPageError("");

    DISPATCH(resetValidateSatisfactionReservationState());
    DISPATCH(resetSubmitSatisfactionFormState());

    try {
      await DISPATCH(validateSatisfactionReservation(reservationId)).unwrap();
    } catch (requestError) {
      setPageError(getApiErrorMessage(requestError, "This feedback form is unavailable."));
    }
  };

  // ----------------------------------------------------
  // ANSWER HANDLING
  // ----------------------------------------------------

  const handleAnswerChange = (questionId, value) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: value,
    }));

    setErrors((current) => ({
      ...current,
      [questionId]: null,
    }));

    if (pageError) {
      setPageError("");
    }
  };

  // ----------------------------------------------------
  // VALIDATION
  // ----------------------------------------------------

  const validate = () => {
    const nextErrors = {};

    const answeredQuestions = questions.filter((question) => {
      const questionId = getQuestionId(question);
      const value = answers[questionId];

      return hasAnswer(question, value);
    });

    if (answeredQuestions.length === 0) {
      setErrors({});

      setPageError("Please answer at least one question before submitting.");

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return false;
    }

    answeredQuestions.forEach((question) => {
      const questionId = getQuestionId(question);
      const questionType = getQuestionType(question);
      const value = answers[questionId];

      if (questionType === "rate_scale") {
        const ratingValue = Number(value);

        if (ratingValue < 1 || ratingValue > 5) {
          nextErrors[questionId] = "Please select a rating from 1 to 5.";
        }

        return;
      }

      if (questionType === "multiple_choice") {
        if (!Array.isArray(value) || value.length === 0) {
          nextErrors[questionId] = "Please select at least one option.";
        }

        return;
      }

      if (questionType === "text_input") {
        const textValue = String(value || "").trim();

        if (textValue.length < 2) {
          nextErrors[questionId] = "Your response is too short.";
        } else if (textValue.length > 2000) {
          nextErrors[questionId] = "Your response cannot exceed 2,000 characters.";
        }
      }
    });

    setErrors(nextErrors);

    const firstErrorQuestionId = Object.keys(nextErrors)[0];

    if (firstErrorQuestionId) {
      setPageError("Please correct the highlighted response before submitting.");

      window.requestAnimationFrame(() => {
        questionRefs.current[firstErrorQuestionId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });

      return false;
    }

    setPageError("");

    return true;
  };

  // ----------------------------------------------------
  // BUILD SUBMISSION PAYLOAD
  // ----------------------------------------------------

  const buildSubmissionAnswers = () =>
    questions
      .filter((question) => {
        const questionId = getQuestionId(question);

        return hasAnswer(question, answers[questionId]);
      })
      .map((question) => {
        const questionId = getQuestionId(question);
        const questionType = getQuestionType(question);
        const rawAnswer = answers[questionId];

        if (questionType === "rate_scale") {
          return {
            questionId,
            questionType,
            value: Number(rawAnswer),
          };
        }

        if (questionType === "multiple_choice") {
          return {
            questionId,
            questionType,
            value: rawAnswer,
          };
        }

        return {
          questionId,
          questionType,
          value: String(rawAnswer).trim(),
        };
      });

  // ----------------------------------------------------
  // SUBMIT
  // ----------------------------------------------------

  const handleSubmit = async () => {
    if (submitFormLoading || !form || !publicValidationResult) {
      return;
    }

    if (!validate()) return;

    const payload = {
      reservationId: publicValidationResult?.reservationId || reservationId,

      answers: buildSubmissionAnswers(),
    };

    try {
      await DISPATCH(submitSatisfactionForm(payload)).unwrap();

      setErrors({});
      setPageError("");
    } catch (requestError) {
      setPageError(
        getApiErrorMessage(
          requestError,
          "We could not submit your feedback. Please review your responses and try again."
        )
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // ----------------------------------------------------
  // INITIALIZE
  // ----------------------------------------------------

  useEffect(() => {
    if (!reservationId) return;

    loadForm();

    return () => {
      DISPATCH(resetValidateSatisfactionReservationState());
      DISPATCH(resetSubmitSatisfactionFormState());
    };
  }, [DISPATCH, reservationId]);

  // ----------------------------------------------------
  // INVALID LINK
  // ----------------------------------------------------

  if (!reservationId) {
    return (
      <StatusState
        icon={<FaExclamationTriangle size={22} />}
        title="Invalid Feedback Link"
        description="The feedback link is missing a reservation ID. Please use the original link sent by the hotel."
      />
    );
  }

  // ----------------------------------------------------
  // LOADING
  // ----------------------------------------------------

  if (validateReservationLoading && !publicValidationResult) {
    return (
      <SatisfactionPageLayout>
        <OverlayLoader
          open
          message="Validating your reservation and loading the feedback form..."
        />
      </SatisfactionPageLayout>
    );
  }

  // ----------------------------------------------------
  // VALIDATION ERRORS
  // ----------------------------------------------------

  if (!validateReservationLoading && (validateReservationError || pageError) && !form) {
    const validationMessage = getApiErrorMessage(
      validateReservationError || pageError,
      "This feedback form is unavailable."
    );

    const normalizedMessage = validationMessage.toLowerCase();

    const alreadySubmitted =
      normalizedMessage.includes("already submitted") ||
      normalizedMessage.includes("already been submitted");

    const cancelledOrNoShow =
      normalizedMessage.includes("cancelled") ||
      normalizedMessage.includes("canceled") ||
      normalizedMessage.includes("no-show") ||
      normalizedMessage.includes("no show");

    if (alreadySubmitted) {
      return (
        <StatusState
          icon={<FaCheck size={22} />}
          title="Feedback Already Submitted"
          description="Feedback has already been submitted for this reservation. Thank you for sharing your experience."
          type="success"
        />
      );
    }

    if (cancelledOrNoShow) {
      return (
        <StatusState
          icon={<FaExclamationTriangle size={22} />}
          title="Feedback Form Unavailable"
          description="This reservation is not eligible for feedback because it was cancelled or marked as a no-show."
          type="warning"
        />
      );
    }

    return (
      <StatusState
        icon={<FaExclamationTriangle size={22} />}
        title="Unable to Load Feedback Form"
        description={validationMessage}
        actionLabel="Try Again"
        onAction={loadForm}
      />
    );
  }

  // ----------------------------------------------------
  // EMPTY FORM
  // ----------------------------------------------------

  if (!validateReservationLoading && publicValidationResult && !form) {
    return (
      <StatusState
        icon={<FaClipboardList size={22} />}
        title="No Active Feedback Form"
        description="There is currently no active satisfaction form available for this reservation."
        type="warning"
      />
    );
  }

  if (!validateReservationLoading && form && totalQuestions === 0) {
    return (
      <StatusState
        icon={<FaClipboardList size={22} />}
        title="No Questions Available"
        description="The feedback form does not currently contain any active questions."
        type="warning"
      />
    );
  }

  // ----------------------------------------------------
  // SUCCESS
  // ----------------------------------------------------

  if (submitFormSuccess || submissionResult) {
    return (
      <StatusState
        icon={<FaCheck size={22} />}
        title="Thank You!"
        centered
        description="Your feedback has been submitted successfully. Your responses will help improve future guest experiences."
        type="success"
      />
    );
  }

  return (
    <SatisfactionPageLayout>
      <Card
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 720,
          mx: "auto",
          p: {
            xs: 2.5,
            sm: 3,
            md: 4,
          },
          backgroundColor: SARTO.white,
          border: `1px solid ${SARTO.border}`,
          borderRadius: "18px",
          boxShadow: "0 4px 24px rgba(27,46,36,0.025)",
          overflow: "hidden",
        }}
      >
        <OverlayLoader open={submitFormLoading} message="Submitting your feedback..." />

        {/* FORM HEADER */}
        <Box sx={{ mb: 3.5 }}>
          <Typography
            sx={{
              fontSize: {
                xs: "23px",
                md: "27px",
              },
              lineHeight: 1.3,
              fontWeight: 500,
              letterSpacing: "-0.6px",
              color: SARTO.green,
              textAlign: "center",
            }}
          >
            {form?.name || "Guest Satisfaction Form"}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              color: SARTO.muted,
              fontSize: {
                xs: "13px",
                md: "14px",
              },
              lineHeight: 1.75,
              maxWidth: 590,
              mx: "auto",
              textAlign: "center",
            }}
          >
            {form?.description ||
              "We would love to hear about your stay. Your feedback helps us improve future guest experiences."}
          </Typography>
        </Box>

        {/* ERRORS */}
        {pageError && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: "10px",
              fontSize: "13px",
            }}
          >
            {pageError}
          </Alert>
        )}

        {submitFormError && !pageError && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: "10px",
            }}
          >
            {getApiErrorMessage(submitFormError, "Unable to submit feedback.")}
          </Alert>
        )}

        {/* PROGRESS */}
        <Box sx={{ mb: 3.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.1,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: SARTO.green,
              }}
            >
              Your Progress
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: SARTO.muted,
              }}
            >
              {answeredCount} of {totalQuestions} answered
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              height: 7,
              borderRadius: "20px",
              backgroundColor: SARTO.progressBg,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${completionPercent}%`,
                backgroundColor: SARTO.green,
                borderRadius: "20px",
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>

        {/* QUESTIONS */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {questions.map((question, index) => {
            const questionId = getQuestionId(question);
            const questionText = getQuestionText(question);
            const questionType = getQuestionType(question);

            const options = Array.isArray(question?.options)
              ? question.options.filter(
                  (option) => typeof option === "string" || option?.isActive !== false
                )
              : [];

            const selectedOptions = Array.isArray(answers[questionId]) ? answers[questionId] : [];

            const hasError = Boolean(errors[questionId]);

            return (
              <Box
                key={questionId || index}
                ref={(element) => {
                  if (questionId) {
                    questionRefs.current[questionId] = element;
                  }
                }}
                sx={{
                  p: {
                    xs: 2,
                    md: 2.5,
                  },
                  borderRadius: "14px",
                  backgroundColor: SARTO.white,
                  border: `1px solid ${hasError ? `${SARTO.error}70` : SARTO.border}`,
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",

                  "&:focus-within": {
                    borderColor: hasError ? SARTO.error : SARTO.green,
                    boxShadow: `0 0 0 2px ${hasError ? `${SARTO.error}10` : `${SARTO.green}08`}`,
                  },
                }}
              >
                {/* QUESTION TITLE */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.7,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: SARTO.muted,
                      fontWeight: 500,
                      flexShrink: 0,
                      pt: "2px",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}.
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: {
                        xs: "14px",
                        md: "15px",
                      },
                      fontWeight: 600,
                      lineHeight: 1.6,
                      color: SARTO.text,
                    }}
                  >
                    {questionText}
                  </Typography>
                </Box>

                {/* RATING */}
                {questionType === "rate_scale" && (
                  <Box sx={{ pl: 2.8 }}>
                    <Rating
                      value={Number(answers[questionId] || 0)}
                      onChange={(_, value) => handleAnswerChange(questionId, value)}
                      max={5}
                      size="large"
                      disabled={submitFormLoading}
                      sx={{
                        color: SARTO.gold,
                        gap: 0.6,

                        "& .MuiRating-iconEmpty": {
                          color: "#91A199",
                        },

                        "& .MuiRating-iconHover": {
                          color: SARTO.gold,
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: SARTO.muted,
                        mt: 0.8,
                      }}
                    >
                      {answers[questionId] ? `${answers[questionId]} out of 5` : "Select a rating"}
                    </Typography>
                  </Box>
                )}

                {/* MULTIPLE CHOICE */}
                {questionType === "multiple_choice" && (
                  <>
                    {options.length > 0 ? (
                      <FormGroup
                        sx={{
                          pl: 1.5,
                          gap: 0.25,
                        }}
                      >
                        {options.map((option, optionIndex) => {
                          const optionId = getOptionId(option);

                          const optionLabel = getOptionLabel(option);

                          const checked = selectedOptions.includes(optionId);

                          return (
                            <FormControlLabel
                              key={optionId || optionIndex}
                              disabled={submitFormLoading}
                              sx={{
                                mx: 0,
                                minHeight: 37,
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",

                                "& .MuiFormControlLabel-label": {
                                  flex: 1,
                                },
                              }}
                              control={
                                <Checkbox
                                  checked={checked}
                                  onChange={(event) => {
                                    const updatedOptions = event.target.checked
                                      ? [...new Set([...selectedOptions, optionId])]
                                      : selectedOptions.filter(
                                          (selectedOptionId) => selectedOptionId !== optionId
                                        );

                                    handleAnswerChange(questionId, updatedOptions);
                                  }}
                                  size="small"
                                  sx={{
                                    color: "#9BA9A1",
                                    flexShrink: 0,
                                    p: 0.8,
                                    mr: 0.8,

                                    "&.Mui-checked": {
                                      color: SARTO.green,
                                    },

                                    "&:hover": {
                                      backgroundColor: `${SARTO.green}08`,
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: checked ? 500 : 400,
                                    color: SARTO.text,
                                  }}
                                >
                                  {optionLabel}
                                </Typography>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    ) : (
                      <Alert severity="warning">
                        No answer options are available for this question.
                      </Alert>
                    )}
                  </>
                )}

                {/* TEXT INPUT */}
                {questionType === "text_input" && (
                  <Box sx={{ pl: 2.8 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={question?.multiline ? 4 : 3}
                      placeholder="Share your thoughts..."
                      value={answers[questionId] || ""}
                      onChange={(event) => handleAnswerChange(questionId, event.target.value)}
                      disabled={submitFormLoading}
                      inputProps={{
                        maxLength: 300,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: SARTO.white,
                          borderRadius: "10px",
                          fontSize: "13px",
                          color: SARTO.text,
                          lineHeight: 1.7,
                          p: 1.8,

                          "& fieldset": {
                            borderColor: SARTO.border,
                          },

                          "&:hover fieldset": {
                            borderColor: "#B7C7BC",
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: SARTO.green,
                            borderWidth: "1px",
                          },
                        },

                        "& .MuiInputBase-input::placeholder": {
                          color: SARTO.muted,
                          opacity: 0.85,
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        textAlign: "right",
                        mt: 0.7,
                        fontSize: "11px",
                        color: SARTO.muted,
                      }}
                    >
                      {String(answers[questionId] || "").length}
                      /300
                    </Typography>
                  </Box>
                )}

                {/* UNSUPPORTED QUESTION */}
                {!["rate_scale", "multiple_choice", "text_input"].includes(questionType) && (
                  <Alert severity="warning">This question type is not currently supported.</Alert>
                )}

                {/* QUESTION ERROR */}
                {errors[questionId] && (
                  <Typography
                    sx={{
                      mt: 1,
                      pl: 2.8,
                      fontSize: "12px",
                      color: SARTO.error,
                      fontWeight: 500,
                    }}
                  >
                    {errors[questionId]}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* SUBMIT SECTION */}
        <Box
          sx={{
            mt: 3.5,
          }}
        >
          <VuiButton
            onClick={handleSubmit}
            disabled={submitFormLoading || totalQuestions === 0}
            fullWidth
            sx={{
              width: "100%",
              minHeight: "48px",
              borderRadius: "10px",
              backgroundColor: `${SARTO.green} !important`,
              color: "#FFFFFF !important",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              transition: "all 0.2s ease",

              "&:hover": {
                backgroundColor: "#294436 !important",
                boxShadow: "0 4px 12px rgba(27,46,36,0.12)",
              },

              "&.Mui-disabled": {
                opacity: 0.55,
                color: "#FFFFFF !important",
              },
            }}
          >
            Submit Feedback
          </VuiButton>

          {/* FOOTER MESSAGE */}
          <Typography
            sx={{
              mt: 2.2,
              textAlign: "center",
              color: SARTO.muted,
              fontSize: "11px",
              lineHeight: 1.8,
            }}
          >
            Thank you for sharing your experience. Your feedback helps us make every stay better.
          </Typography>
        </Box>
      </Card>
    </SatisfactionPageLayout>
  );
};

export default GuestSatisfactionFormSubmission;
